// Core
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  openNotificationPopup,
  openWarningAlert,
  selectCommunityListSort,
  selectThemeMode,
  setCommunityListSort,
  setThemeMode,
} from "state/application/applicationSlice";
import { selectCurrentUser, selectRole } from "state/auth/authSlice";
import {
  useAddNewCommunityMutation,
  useDeleteCommunityByIdMutation,
  useGetCommunitiesByUserQuery,
} from "state/communities/communitiesApiSlice";
import { resetCommunity, setCommunity } from "state/communities/communitiesSlice";

// Casl
import { AbilityContext } from "context/Ability.context";

// Utils
import { getUserRoleLabelFromValue } from "utils/constants/helpers";
import {
  ALLOCATION_TYPES,
  SIMULATOR_IMPORT_VERSION as IMPORT_VERSION,
} from "utils/constants/index";
import { translateOptions } from "utils/constants/enumLabels";

// Components
import {
  Avatar,
  Button,
  FieldSelect,
  FieldText,
  FieldToggle,
  Modal,
  Pager,
  ValidityDateRangePicker,
} from "components/AtomsV2";
import { BrandLogo, Icon, LanguageMenu } from "components/ShellV2";
import ImportVersionError from "pages/Simulator/ImportExport/exception";
import { defaultBusinessEndDate, lisbonToday } from "utils/businessValidity";

// Assets
import inesctecLogo from "assets/images/inesctec.png";

import CommunityCard from "./components/CommunityCard";

const COMMUNITIES_PER_PAGE = 12;
const DEFAULT_COMMUNITY_START_DATE = lisbonToday();

const RESOURCE_ICONS = {
  EV: "electric_car",
  STORAGE: "battery_charging_full",
  SCPU: "wb_sunny",
  EWH: "water_heater",
  IU: "home",
  IC: "bolt",
  HP: "heat_pump",
};

function NewCommunityModal({ open, onClose, onCreate }) {
  const { t } = useTranslation(["community", "common"]);
  const [form, setForm] = useState({
    name: "",
    identifier: "",
    alloc: ALLOCATION_TYPES[1].value,
    demo: false,
    startDate: DEFAULT_COMMUNITY_START_DATE,
    endDate: defaultBusinessEndDate(DEFAULT_COMMUNITY_START_DATE),
  });

  useEffect(() => {
    if (open) {
      setForm({
        name: "",
        identifier: "",
        alloc: ALLOCATION_TYPES[1].value,
        demo: false,
        startDate: DEFAULT_COMMUNITY_START_DATE,
        endDate: defaultBusinessEndDate(DEFAULT_COMMUNITY_START_DATE),
      });
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("new.title")}
      subtitle={t("new.subtitle")}
      maxWidth={460}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("new.cancel")}
          </Button>
          <Button
            disabled={
              !form.name.trim() ||
              !form.identifier.trim() ||
              !form.startDate ||
              (form.endDate && form.endDate < form.startDate)
            }
            onClick={() => onCreate(form)}
          >
            <Icon name="add" />
            {t("new.create_long")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-[14px]">
        <FieldText
          label={t("new.legal_name")}
          value={form.name}
          placeholder={t("new.name_placeholder")}
          onChange={(v) => setForm((s) => ({ ...s, name: v }))}
          required
        />
        <FieldText
          label={t("new.identifier")}
          value={form.identifier}
          placeholder={t("new.identifier_placeholder")}
          onChange={(v) => setForm((s) => ({ ...s, identifier: v }))}
          required
        />
        <FieldSelect
          label={t("new.alloc_type")}
          value={form.alloc}
          options={translateOptions(ALLOCATION_TYPES, "allocation")}
          onChange={(v) => setForm((s) => ({ ...s, alloc: v }))}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[14px]">
          <ValidityDateRangePicker
            className="sm:col-span-2"
            value={{ start: form.startDate, end: form.endDate }}
            onChange={({ start, end }) =>
              setForm((current) => ({ ...current, startDate: start, endDate: end }))
            }
          />
        </div>
        <FieldToggle
          label={t("new.demo_toggle")}
          desc={t("new.demo_desc")}
          value={form.demo}
          onChange={(v) => setForm((s) => ({ ...s, demo: v }))}
        />
      </div>
    </Modal>
  );
}

NewCommunityModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
};

function ListCommunities() {
  const user = useSelector(selectCurrentUser);
  const role = useSelector(selectRole);
  const ability = useContext(AbilityContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const themeMode = useSelector(selectThemeMode);
  const isDark = themeMode === "dark";
  const toggleTheme = () => dispatch(setThemeMode(isDark ? "light" : "dark"));

  const { t } = useTranslation(["community", "sidenav"]);

  const [modal, setModal] = useState(false);

  const navigateToSimulation = () => navigate(`/communities/simulator/`);

  const fileInputRef = useRef(null);
  const handleLoadCommunity = () => fileInputRef.current?.click();
  const onFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target.result);
        const data = parsed.simulator ?? parsed;
        if (!data.import_version || data.import_version !== IMPORT_VERSION) {
          throw new ImportVersionError(
            t("import_export.errors.invalid_import_version", {
              ns: "simulator",
              defaultValue: "Invalid import version",
            })
          );
        }
        navigate(`/communities/simulator/`, {
          state: {
            preloadedData: {
              basicInfo: data.basicInfo ?? {},
              members: data.members ?? [],
              resources: data.resources ?? [],
              meters: data.meters ?? [],
              contracts: data.contracts ?? [],
              resp: data.resp ?? [],
              fixedBilateralPrices: data.fixedBilateralPrices ?? [],
            },
          },
        });
      } catch (err) {
        if (err.name === "ImportVersionError") {
          dispatch(
            openNotificationPopup({
              notification: t("import_export.notifications.invalid_version", {
                ns: "simulator",
              }),
              type: "error",
            })
          );
        } else {
          dispatch(
            openNotificationPopup({
              notification: t("import_export.notifications.error", {
                ns: "simulator",
                defaultValue: "Erro ao importar",
              }),
              type: "error",
            })
          );
        }
      }
    };
    reader.onerror = () => {
      dispatch(
        openNotificationPopup({
          notification: t("import_export.notifications.error", {
            ns: "simulator",
            defaultValue: "Erro ao importar",
          }),
          type: "error",
        })
      );
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  // ── DELETE community ───────────────────────────────────────────────────────
  const [deleteCommunity] = useDeleteCommunityByIdMutation();
  const [deletingIds, setDeletingIds] = useState([]);
  const handleDeleteCommunity = (community) => {
    dispatch(
      openWarningAlert({
        title: t("delete.title"),
        text: t("delete.warning"),
        confirmText: t("delete.confirm_button"),
        confirmAction: async () => {
          setDeletingIds((ids) => [...ids, community.id]);
          try {
            await deleteCommunity({ id: community.id }).unwrap();
          } catch (err) {
            dispatch(openNotificationPopup({ notification: err, type: "error" }));
          } finally {
            setDeletingIds((ids) => ids.filter((id) => id !== community.id));
          }
        },
      })
    );
  };

  // ── READ communities ───────────────────────────────────────────────────────
  const {
    data: communities,
    isSuccess,
    isLoading,
  } = useGetCommunitiesByUserQuery(undefined, {
    refetchOnFocus: true,
  });

  // ── CREATE community ───────────────────────────────────────────────────────
  const [addNewCommunity] = useAddNewCommunityMutation();
  const onNewCommunity = async (form) => {
    setModal(false);
    try {
      await addNewCommunity({
        name: form.name,
        identifier: form.identifier.trim(),
        type: form.demo ? "DEMO" : "PROD",
        method_alloc_coeff: form.alloc,
        start_date: form.startDate,
        end_date: form.endDate || null,
      }).unwrap();
    } catch (err) {
      dispatch(openNotificationPopup({ notification: err, type: "error" }));
    }
  };

  useEffect(() => {
    dispatch(resetCommunity());
  }, []);

  const canSimulate =
    user.permissions.find((permission) => permission.name === "SIMULATION_MODULE")?.is_active ||
    user.role.name === "SUPER_USER";
  const isSuperUser = user.role.name === "SUPER_USER";

  // Auto-redirect MEMBER to their community dashboard
  useEffect(() => {
    if (isSuccess && role === "MEMBER" && communities?.ids) {
      const memberIds = communities.ids.filter((id) => id !== "NEW");
      if (memberIds.length > 0) {
        const community = communities.entities[memberIds[0]];
        dispatch(setCommunity({ community }));
        navigate(`/communities/${community.id}`);
      }
    }
  }, [isSuccess, role, communities, dispatch, navigate]);

  // ── Filters & search ───────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const { field: sortField, dir: sortDir } = useSelector(selectCommunityListSort);
  const [resourceTypeFilters, setResourceTypeFilters] = useState([]);

  const toggleSort = (field) => {
    if (sortField !== field) {
      dispatch(setCommunityListSort({ field, dir: "asc" }));
    } else if (sortDir === "asc") {
      dispatch(setCommunityListSort({ field, dir: "desc" }));
    } else {
      dispatch(setCommunityListSort({ field: null, dir: "asc" }));
    }
  };

  const realCommunities = useMemo(() => {
    if (!isSuccess || !communities) return [];
    return communities.ids.filter((id) => id !== "NEW").map((id) => communities.entities[id]);
  }, [isSuccess, communities]);

  const availableResourceTypes = useMemo(() => {
    const types = new Set();
    realCommunities.forEach((c) => {
      (c.resource_types || []).forEach((rt) => types.add(rt));
    });
    return Array.from(types).sort();
  }, [realCommunities]);

  const toggleResourceType = (type) => {
    setResourceTypeFilters((prev) =>
      prev.includes(type) ? prev.filter((rt) => rt !== type) : [...prev, type]
    );
  };

  const filteredCommunities = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLocaleLowerCase();
    let result = realCommunities.filter((c) => {
      if (
        normalizedSearch &&
        ![c.name, c.identifier].some((value) =>
          value?.toLocaleLowerCase().includes(normalizedSearch)
        )
      ) {
        return false;
      }
      if (resourceTypeFilters.length > 0) {
        const communityTypes = c.resource_types || [];
        if (!resourceTypeFilters.every((rt) => communityTypes.includes(rt))) return false;
      }
      return true;
    });
    if (sortField) {
      result = [...result].sort((a, b) => {
        let aVal;
        let bVal;
        if (sortField === "date") {
          aVal = new Date(a.created_at || 0).getTime();
          bVal = new Date(b.created_at || 0).getTime();
        } else {
          aVal = a.total_members ?? 0;
          bVal = b.total_members ?? 0;
        }
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      });
    }
    return result;
  }, [realCommunities, searchQuery, sortField, sortDir, resourceTypeFilters]);

  // ── Pagination ─────────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const pages = Math.max(1, Math.ceil(filteredCommunities.length / COMMUNITIES_PER_PAGE));
  const safePage = Math.min(page, pages);
  const pagedCommunities = useMemo(
    () =>
      filteredCommunities.slice(
        (safePage - 1) * COMMUNITIES_PER_PAGE,
        safePage * COMMUNITIES_PER_PAGE
      ),
    [filteredCommunities, safePage]
  );

  useEffect(() => {
    setPage(1);
  }, [searchQuery, resourceTypeFilters, sortField, sortDir]);

  const hasNewSlot = communities?.ids?.includes("NEW");
  const canCreate = ability.can("create", "communityCreation");

  const selectCommunity = (community) => {
    dispatch(setCommunity({ community }));
    navigate(`/communities/${community.id}`);
  };
  const configureCommunity = (community) => {
    dispatch(setCommunity({ community }));
    navigate(`/communities/${community.id}/configuration`);
  };

  return (
    <div className="rec-comm-screen">
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        style={{ display: "none" }}
        onChange={onFileSelected}
      />

      <header className="rec-comm-top">
        {/* forceWhite follows only the theme since this header is always a
            light/dark surface, never brand-colored */}
        <BrandLogo height={58} forceWhite={isDark} />
        <div className="flex-1" />
        {isSuperUser && (
          <Button
            variant="ghost"
            size="sm"
            className="mr-1"
            onClick={() => navigate("/communities/admin")}
          >
            <Icon name="admin_panel_settings" />
            {t("list.actions.admin")}
          </Button>
        )}
        <LanguageMenu />
        <button
          type="button"
          className="rec-icon-btn"
          onClick={toggleTheme}
          title={t("list.actions.theme_toggle")}
        >
          <Icon name={isDark ? "light_mode" : "dark_mode"} fill />
        </button>
        <button
          type="button"
          className="rec-icon-btn"
          onClick={() => navigate("/signout")}
          title={t("topbar.logout", { ns: "sidenav", defaultValue: "Sair" })}
        >
          <Icon name="logout" />
        </button>
        <div className="w-px h-[30px] bg-border mx-1" aria-hidden="true" />
        <div className="flex items-center gap-[10px]">
          <Avatar name={user.name} size="md" />
          <div className="leading-[1.2]">
            <div className="text-[13px] font-semibold text-ink">{user.name}</div>
            <div className="text-[11px] text-ink-3">{getUserRoleLabelFromValue(role)}</div>
          </div>
        </div>
      </header>

      <div className="rec-comm-body">
        <div className="rec-comm-hero">
          <div>
            <h1>{t("list.hero.title")}</h1>
            <p>{t("list.hero.subtitle")}</p>
          </div>
          <div className="flex gap-[10px] flex-wrap">
            {canSimulate && (
              <Button variant="ghost" onClick={navigateToSimulation}>
                <Icon name="play_circle" />
                {t("list.new.simulate_title")}
              </Button>
            )}
            {canSimulate && (
              <Button variant="ghost" onClick={handleLoadCommunity}>
                <Icon name="upload_file" />
                {t("list.actions.load")}
              </Button>
            )}
            {canCreate && (
              <Button onClick={() => setModal(true)}>
                <Icon name="add" />
                {t("list.new.title")}
              </Button>
            )}
          </div>
        </div>

        <div className="rec-comm-toolbar">
          <div className="rec-input-wrap flex-1 min-w-[220px] max-w-[380px]">
            <Icon name="search" />
            <input
              className="rec-input"
              placeholder={t("list.filter.search")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          {availableResourceTypes.map((type) => (
            <button
              key={type}
              type="button"
              className={`rec-filter-ic${resourceTypeFilters.includes(type) ? " on" : ""}`}
              title={t(`list.card.resource_types.${type}`, { defaultValue: type })}
              onClick={() => toggleResourceType(type)}
            >
              <Icon name={RESOURCE_ICONS[type] || "category"} style={{ fontSize: 20 }} />
            </button>
          ))}
          <button
            type="button"
            className={`rec-filter-ic${sortField === "date" ? " on" : ""}`}
            title={t("list.filter.sort_date")}
            onClick={() => toggleSort("date")}
          >
            <Icon name="calendar_today" style={{ fontSize: 19 }} />
            {sortField === "date" && (
              <Icon
                name={sortDir === "asc" ? "arrow_upward" : "arrow_downward"}
                className="sortdir"
              />
            )}
          </button>
          <button
            type="button"
            className={`rec-filter-ic${sortField === "members" ? " on" : ""}`}
            title={t("list.filter.sort_members")}
            onClick={() => toggleSort("members")}
          >
            <Icon name="group" style={{ fontSize: 20 }} />
            {sortField === "members" && (
              <Icon
                name={sortDir === "asc" ? "arrow_upward" : "arrow_downward"}
                className="sortdir"
              />
            )}
          </button>
          <div className="ml-auto text-[12.5px] text-ink-3 font-semibold">
            {`${filteredCommunities.length} ${t("list.filter.results")}`}
          </div>
        </div>

        {isLoading && (
          <div className="flex flex-col items-center gap-[12px] py-[70px] text-ink-3">
            <span className="animate-spin inline-flex">
              <Icon name="progress_activity" style={{ fontSize: 34, color: "var(--accent)" }} />
            </span>
            <div className="text-[13.5px] font-semibold">{t("list.loading")}</div>
          </div>
        )}
        {!isLoading && (filteredCommunities.length > 0 || hasNewSlot) ? (
          <>
            <div className="rec-comm-grid">
              {pagedCommunities.map((community) => (
                <CommunityCard
                  key={community.id}
                  community={community}
                  canDelete={role === "SUPER_USER"}
                  deleting={deletingIds.includes(community.id)}
                  onSelect={selectCommunity}
                  onConfigure={configureCommunity}
                  onDelete={handleDeleteCommunity}
                />
              ))}
              {hasNewSlot && (
                <div className="rec-comm-card animate-pulse items-center justify-center min-h-[180px]">
                  <div className="flex flex-col items-center gap-2 text-ink-3">
                    <Icon name="hourglass_top" style={{ fontSize: 30 }} />
                    <span className="text-[13px] font-semibold">{`${t(
                      "list.waiting.creating"
                    )}…`}</span>
                  </div>
                </div>
              )}
            </div>
            {pages > 1 && (
              <div className="flex justify-center mt-[18px]">
                <Pager page={safePage} pages={pages} onChange={setPage} />
              </div>
            )}
          </>
        ) : (
          isSuccess && (
            <div className="flex flex-col items-center gap-[10px] py-[60px] text-ink-3">
              <Icon name="search_off" style={{ fontSize: 42, opacity: 0.6 }} />
              <div className="text-[14px]">{t("list.filter.no_results")}</div>
            </div>
          )
        )}

        <div className="rec-comm-foot-logo">
          <span>{t("list.credit")}</span>
          <img src={inesctecLogo} alt="INESC TEC" />
        </div>
      </div>

      <NewCommunityModal open={modal} onClose={() => setModal(false)} onCreate={onNewCommunity} />
    </div>
  );
}

export default ListCommunities;
